import { browser } from '$app/environment'
import { get, writable } from 'svelte/store'

export type ThemePreference = 'light' | 'dark' | 'system'
export type ThemeMode = 'light' | 'dark'

interface ThemeState {
  preference: ThemePreference
  mode: ThemeMode
}

const THEME_STORAGE_KEY = 'hirundo-theme-preference'
const PREFERENCE_ORDER: ThemePreference[] = ['light', 'dark', 'system']

const themeState = writable<ThemeState>({
  preference: 'system',
  mode: 'dark',
})

let initialized = false

function resolveMode(preference: ThemePreference): ThemeMode {
  if (preference === 'system') {
    if (!browser) {
      return 'dark'
    }
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
  }
  return preference
}

function applyMode(mode: ThemeMode) {
  if (!browser) {
    return
  }
  const root = document.documentElement
  root.classList.toggle('dark', mode === 'dark')
  root.dataset.themeMode = mode
}

function persistPreference(preference: ThemePreference) {
  if (!browser) {
    return
  }
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, preference)
  } catch {
    // Storage might be unavailable (e.g., in private browsing). Ignore errors.
  }
}

function readStoredPreference(): ThemePreference | null {
  if (!browser) {
    return null
  }
  try {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY)
    return stored === 'light' || stored === 'dark' || stored === 'system' ? stored : null
  } catch {
    return null
  }
}

function setState(preference: ThemePreference) {
  const mode = resolveMode(preference)
  applyMode(mode)
  themeState.set({ preference, mode })
  persistPreference(preference)
}

export const themeStore = {
  subscribe: themeState.subscribe,
  setPreference: (preference: ThemePreference) => {
    setState(preference)
  },
  cyclePreference: () => {
    const current = get(themeState).preference
    const index = PREFERENCE_ORDER.indexOf(current)
    const next = PREFERENCE_ORDER[(index + 1) % PREFERENCE_ORDER.length]
    setState(next)
  },
  initialize: () => {
    if (!browser || initialized) {
      return () => undefined
    }
    initialized = true
    const stored = readStoredPreference()
    setState(stored ?? 'system')
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    const handleSystemChange = (event: MediaQueryListEvent) => {
      if (get(themeState).preference === 'system') {
        const mode = event.matches ? 'dark' : 'light'
        applyMode(mode)
        themeState.update((current) => ({ ...current, mode }))
      }
    }
    mediaQuery.addEventListener('change', handleSystemChange)
    return () => {
      mediaQuery.removeEventListener('change', handleSystemChange)
      initialized = false
    }
  },
}
